<template>
  <div class="vue-carousel">
    <div class="vue-carousel-box">
      <div
        v-if="hasHiddenCard"
        class="vue-carousel__arrow vue-carousel__prev"
        :class="{ disabled: currentSlide === 0 }"
        @click="prev"
      >
        <ArrowLeft />
      </div>

      <Carousel
        ref="carouselRef"
        v-model="currentSlide"
        :breakpoints="breakpoints"
        @slideEnd="slideEnd"
      >
        <Slide
          v-for="(card, index) in list"
          :key="index"
          class="slideEl"
        >
          <RecentCard
            :card="card as LabData"
            :disableStorageUpdate="true"
            class="carousel__slide"
          />
        </Slide>
      </Carousel>

      <div
        v-if="hasHiddenCard"
        class="vue-carousel__arrow vue-carousel__next"
        :class="{ disabled: isRightArrowDisabled }"
        @click="next"
      >
        <ArrowLeft />
      </div>
    </div>

    <CarouselPagination
      ref="paginationRef"
      :carouselRef="carouselRef"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import 'vue3-carousel/dist/carousel.css';
import { Carousel, Slide } from 'vue3-carousel';
import { LabData } from '@/types/LabData';
import ArrowLeft from '@/components/icons/CarouselLeft.vue';
import RecentCard from '@/components/Carousel/RecentCard.vue';
import CarouselPagination from '@/components/Carousel/CarouselPagination.vue';

const props = defineProps<{
  list: LabData[];
}>();

const currentSlide = ref(0);
const carouselRef = ref();
const paginationRef = ref();
const breakpoints = ref();
const hasHiddenCard = computed(() => paginationRef.value?.hasHiddenCard ?? false);

onMounted(() => {
  breakpoints.value = {
    // 300 and up
    300: {
      itemsToShow: carouselRef.value.data.maxSlide.value > 0 ? 1.25 : 1,
      snapAlign: 'start',
    },
    575: {
      itemsToShow: 2,
      snapAlign: 'start',
    },
    800: {
      itemsToShow: 3,
      snapAlign: 'start',
    },
    1080: {
      itemsToShow: 4,
      snapAlign: 'start',
    },
    1280: {
      itemsToShow: 5,
      snapAlign: 'start',
    },
    1600: {
      itemsToShow: 7,
      snapAlign: 'start',
    },
  };
});

const slideEnd = () => {
  paginationRef.value.updVisibleCards();
};

const findIndex = () => {
  const visibleCards = paginationRef.value?.visibleCards || [];
  const index = visibleCards.findIndex((x: boolean) => x);
  return index < 0 ? 0 : index;
};

const navigate = (direction: number) => {
  const currentIndex = findIndex();
  const newIndex = currentIndex + direction;
  carouselRef.value.slideTo(newIndex);
};

const prev = () => {
  navigate(-1);
};
const next = () => {
  navigate(1);
};

const isRightArrowDisabled = computed(() => {
  const visibleCount = paginationRef.value?.visibleCards.filter((i) => i).length;
  return currentSlide.value === props.list.length - visibleCount;
});
</script>

<style lang="scss">
@import "@/assets/style/include";

.vue-carousel {

  .carousel {
    width: 100%;
  }

  .is-dragging {
    pointer-events: none;
  }

  &-box {
    width: calc(100% + $carouselGap);
    display: flex;
    align-items: center;
    position: relative;
  }

  .pagination {
    display: flex;
    justify-content: center;
    margin-top: 8px;

    &-item {
      padding: 6px;
      cursor: pointer;

      &::before {
        width: 10px;
        height: 10px;
        content: '';
        display: block;
        border: 1px solid $colorBlue1;
        transform: rotate(45deg);
        transition: background .2s;
      }

      &.disabled {
        cursor: auto;

        &::before {
          background-color: $colorBlue1;
          border-color: $colorBlue1;
        }
      }

      &:hover:not(.disabled) {
        &::before {
          background-color: $colorBlueHover;
          border-color: $colorBlueHover;
        }
      }
    }
  }

  &__arrow {
    height: 100px;
    color: $colorBlue1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: -40px;

    &.disabled {
      color: $colorGrayDisabled;

      &:hover, &:active {
        color: $colorGrayDisabled;
      }
    }

    & svg {
      width: 44px;
    }

    &:hover {
      color: $colorBlueHover;
    }
    &:active {
      color: $colorBluePressed;
    }
  }
  &__next {
    transform: rotate(180deg);
    left: auto;
    right: -26px;
  }

  .carousel__pagination-button {
    padding: 10px;
  }
}

@include media-breakpoint-down("tablet") {
  .vue-carousel {

    &__arrow {
      display: none;
    }
  }
}
@include media-breakpoint-down("mobile") {
  .vue-carousel {

    .pagination {
      display: none;
    }

    .carousel {
      width: 100%;
    }
  }
}
</style>
